import { apifetch } from "../../services/interceptors";

// Thin wrapper over apifetch.
//
// Every caller in v1 hand-writes the same Authorization header and the same
// response-ok / json dance, which is why the same three lines appear a hundred times
// across the old screens. This does that once.
//
// Every path is made absolute here. A path without a leading slash is a RELATIVE URL: the
// browser resolves it against the current page and drops the last segment, so the same
// call reaches a different host path depending on which screen made it.
//
//   at /dashboard              "s2/api/tenant" -> /s2/api/tenant          (works)
//   at /settings/integrations  "s2/api/tenant" -> /settings/s2/api/tenant (404 -> index.html)
//
// That is why this only started failing: v2 screens used to live at /dashboard, one
// segment deep, where relative resolution happened to produce the right URL. Giving each
// screen a real path (/settings/integrations, /reviews/overview) broke every slash-less
// call on a two-segment route at once.
//
// An earlier note here claimed `s2/api/...` and `/s2/api/...` were different routes and
// must not be normalised. They are not: the server registers routes, the client builds a
// URL, and "/s2/api/x" is the same URL from every screen. The scan-link calls that note
// worried about already pass a leading slash, so they are unaffected.
const absolute = (path) => {
  const p = String(path ?? '');
  // Absolute URLs and protocol-relative ones are already resolved; leave them alone.
  if (/^([a-z][a-z0-9+.-]*:)?\/\//i.test(p)) return p;
  return p.startsWith('/') ? p : `/${p}`;
};

const authHeaders = (extra = {}) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  ...extra,
});

async function request(rawPath, { method = "GET", body, headers, raw = false } = {}) {
  const path = absolute(rawPath);
  const res = await apifetch(path, {
    method,
    headers: raw ? { Authorization: `Bearer ${localStorage.getItem("token")}`, ...headers } : authHeaders(headers),
    // FormData must go through untouched: setting Content-Type strips the multipart
    // boundary the browser generates, and the upload silently fails.
    body: raw ? body : body != null ? JSON.stringify(body) : undefined,
  });

  // An HTML body on a successful response means the request never reached a service:
  // the dev server and the SPA host both answer an unmatched path with index.html at
  // status 200. Left alone that parses to null, gets swallowed as "no data", and the
  // caller silently behaves as though the endpoint returned nothing — which is how a
  // Pro tenant ended up being shown community templates. Fail loudly instead.
  const contentType = res.headers.get("content-type") || "";
  if (res.ok && contentType.includes("text/html")) {
    throw new Error(
      `${method} ${path} was answered with a page, not data — the route is not served by ` +
      `any API service. Check the path and that the service behind it is running.`
    );
  }

  let data = null;
  try { data = await res.json(); } catch (e) { /* 204, or a non-JSON error page */ }

  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `${method} ${path} failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  get:  (p, o)       => request(p, { ...o, method: "GET" }),
  post: (p, body, o) => request(p, { ...o, method: "POST", body }),
  put:  (p, body, o) => request(p, { ...o, method: "PUT", body }),
  // PATCH is a partial update: send only the fields that changed. Used by the business
  // review editor, where one rating is saved at a time.
  patch: (p, body, o) => request(p, { ...o, method: "PATCH", body }),
  del:  (p, body, o) => request(p, { ...o, method: "DELETE", body }),
  // Multipart: caller passes a FormData and we stay out of the way.
  upload: (p, formData) => request(p, { method: "POST", body: formData, raw: true }),
};
